import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Input, Label, Button } from "reactstrap";
import DeployApi from "../services/appDeployApi";

function AppBuildDetail({
  subdomain,
  buildVersion,
  buildNotes: originalBuildNotes,
  releaseVersion,
  releaseNotes,
  platform,
  storeId,
  distributionAppId,
  notify,
}) {
  const [buildNotes, setBuildNotes] = useState(originalBuildNotes);

  useEffect(() => {
    if (subdomain && buildVersion && !storeId) {
      notify({
        text: `Store must be configured in MS App Center for ${platform} before releasing.`,
      });
    }

    if (subdomain && buildVersion && !distributionAppId) {
      notify({
        text: `${platform} build must complete before releasing ${platform} version.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain, buildVersion, storeId, distributionAppId, platform]);

  useEffect(() => {
    setBuildNotes(originalBuildNotes);
  }, [originalBuildNotes]);

  const clickBuild = () => {
    if (!buildNotes) {
      notify({
        color: "danger",
        text: `Build notes are required to trigger an ${platform} build`,
      });
      return;
    }
    notify({ text: `Attempting to trigger ${platform} build.` });
    DeployApi.buildApp(subdomain, platform.toLowerCase(), buildNotes)
      .then(() =>
        notify({
          color: "success",
          text: `Triggered new ${platform} build. Wait 15-20 minutes to refresh.`,
        })
      )
      .catch((err) => {
        console.log(err?.response?.data);
        notify({
          color: "danger",
          text: `An error occurred triggering ${platform} build.`,
        });
      });
  };

  const clickRelease = () => {
    notify({ text: `Attempting to trigger ${platform} release.` });
    DeployApi.releaseApp(distributionAppId)
      .then(() =>
        notify({
          color: "success",
          text: `Triggered new ${platform} release. Wait 1 minute before refreshing.`,
        })
      )
      .catch((err) => {
        console.log(err?.response?.data);
        notify({
          color: "danger",
          text: `An error occurred attempting to trigger ${platform} release.`,
        });
      });
  };

  const clickScreenshot = () => {
    notify({
      text: `Starting ${platform} screenshots. Screenshots will timeout.`,
    });
    DeployApi.createAppScreenshots(subdomain, platform.toLowerCase())
      .then(() => {
        notify({
          text: `${platform} Screenshots complete.`,
        });
      })
      .catch((err) => {
        if (/^timeout of \d+ms exceeded$/g.test(err?.message || "")) {
          notify({
            color: "warning",
            text: "Screenshots should complete in about 1 minute.",
          });
          return;
        }
        console.log({ err });
        console.log({ response: err.response });
        console.log(err?.response?.data);
        notify({
          color: "danger",
          text: `Error taking ${platform} screenshots.`,
        });
      });
  };

  const releaseDisabled =
    buildVersion === "" ||
    buildVersion === "0.0.0" ||
    buildVersion === releaseVersion ||
    !storeId ||
    !distributionAppId;

  return (
    <div>
      {buildVersion === "" && <div>...waiting for {platform} information</div>}
      {buildVersion !== "" && (
        <div>
          <Row>
            <Col>{platform}</Col>
          </Row>
          <Row>
            <Col md={4}>
              <Label for="latestBuildVersion">Build Version</Label>
            </Col>
            <Col md={8}>
              <Label name="latestReleaseVersion">{buildVersion}</Label>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Label for="buildNotes">Build Notes</Label>
            </Col>
            <Col md={8}>
              <Input
                name="buildNotes"
                type="textarea"
                placeholder={`${platform} Build Notes`}
                onChange={(e) => setBuildNotes(e.target.value)}
                value={buildNotes}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 8, marginBottom: 8 }}>
            <Col align="right">
              <Button color="primary" onClick={clickBuild}>
                Build
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                color="primary"
                disabled={releaseDisabled}
                onClick={clickRelease}
              >
                Release
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                color="primary"
                onClick={clickScreenshot}
              >
                Screenshot
              </Button>{" "}
            </Col>
          </Row>
          {releaseVersion && (
            <Row>
              <Col md={4}>
                <Label for="latestReleaseVersion">Release Version</Label>
              </Col>
              <Col md={8}>
                <Label name="latestReleaseVersion">{releaseVersion}</Label>
              </Col>
            </Row>
          )}
          {releaseVersion && (
            <Row>
              <Col md={4}>
                <Label for="releaseNotes">Release Notes</Label>
              </Col>
              <Col md={8}>
                <Input
                  name="releaseNotes"
                  type="textarea"
                  placeholder={`${platform} Release Notes`}
                  value={releaseNotes}
                  disabled
                />
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );
}

AppBuildDetail.propTypes = {
  subdomain: PropTypes.string.isRequired,
  buildVersion: PropTypes.string.isRequired,
  buildNotes: PropTypes.string.isRequired,
  releaseVersion: PropTypes.string.isRequired,
  releaseNotes: PropTypes.string.isRequired,
  platform: PropTypes.string.isRequired,
  storeId: PropTypes.string.isRequired,
  distributionAppId: PropTypes.string.isRequired,
  notify: PropTypes.func.isRequired,
};

export default AppBuildDetail;
